import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, X, Send, Paperclip, Mic, Square, ArrowLeft, Hash, FileText, Check, CheckCheck } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const isAudio = (f) => f && ((f.content_type || "").startsWith("audio") || /\.(webm|mp3|ogg|wav|m4a)$/i.test(f.name || ""));

function Avatar({ photo, name, size = 36 }) {
  const initial = (name || "?").slice(0, 1).toUpperCase();
  const style = { width: size, height: size };
  if (photo) return <img src={fileUrl(photo)} alt={name} style={style} className="rounded-full object-cover border border-border shrink-0" />;
  return <div style={style} className="rounded-full bg-secondary text-foreground/80 flex items-center justify-center font-medium text-xs shrink-0">{initial}</div>;
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { if (open) api.get("/chat/users").then((r) => setUsers(r.data)).catch(() => {}); }, [open]);

  const photoMap = {};
  users.forEach((u) => { if (u.photo_path) photoMap[u.id] = u.photo_path; });
  if (user?.photo_path) photoMap[user.id] = user.photo_path;

  const paramStr = active?.type === "dm" ? `withUser=${active.u.id}` : "channel=team";
  const load = () => { if (!active) return; api.get(`/chat/messages?${paramStr}`).then((r) => setMessages(r.data)).catch(() => {}); };
  useEffect(() => {
    if (view !== "chat" || !active) return;
    load(); const t = setInterval(load, 3000); return () => clearInterval(t);
    // eslint-disable-next-line
  }, [view, active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConv = (conv) => { setActive(conv); setMessages([]); setView("chat"); };

  const send = async (file) => {
    if (!text.trim() && !file) return;
    const body = active.type === "dm" ? { to_user: active.u.id } : { channel: "team" };
    try { await api.post("/chat/messages", { ...body, text: text.trim(), file: file || null }); setText(""); load(); }
    catch (e) { toast.error("Message failed"); }
  };

  const uploadAndSend = async (fileObj) => {
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", fileObj); const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); await send(data); }
    catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const onFile = async (e) => { const f = e.target.files?.[0]; if (f) await uploadAndSend(f); if (fileRef.current) fileRef.current.value = ""; };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: "audio/webm" });
        await uploadAndSend(file);
      };
      mr.start(); recorderRef.current = mr; setRecording(true);
    } catch (e) { toast.error("Microphone unavailable"); }
  };
  const stopRec = () => { recorderRef.current?.stop(); setRecording(false); };

  const headerTitle = view === "chat" ? (active.type === "team" ? "Team Channel" : active.u.name) : "Team Chat";

  const readByOthers = (m) => {
    const rb = m.read_by || [];
    if (!active) return false;
    if (active.type === "dm") return rb.includes(active.u.id);
    const others = users.map((u) => u.id);
    return others.length > 0 && others.every((id) => rb.includes(id));
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[380px] h-[560px] max-h-[78vh] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200" data-testid="chat-widget">
          {/* header */}
          <div className="bg-gradient-to-r from-primary to-primary/85 text-primary-foreground px-4 py-3 flex items-center gap-3">
            {view === "chat" && <button onClick={() => setView("list")} className="hover:opacity-80" data-testid="chat-back"><ArrowLeft size={18} /></button>}
            {view === "chat" && (active.type === "team"
              ? <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Hash size={18} /></div>
              : <Avatar photo={active.u.photo_path} name={active.u.name} size={36} />)}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-sm truncate leading-tight">{headerTitle}</p>
              {view === "chat" && <p className="text-[11px] opacity-80 truncate">{active.type === "team" ? "Everyone on the team" : (active.u.role || "Online")}</p>}
            </div>
            <button onClick={() => setOpen(false)} className="hover:opacity-80" data-testid="chat-close"><X size={18} /></button>
          </div>

          {view === "list" ? (
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5" data-testid="chat-conv-list">
              <button onClick={() => openConv({ type: "team" })} data-testid="chat-open-team" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent text-left transition-colors">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Hash size={18} /></div>
                <div><p className="text-sm font-semibold">Team Channel</p><p className="text-xs text-muted-foreground">Message everyone</p></div>
              </button>
              <div className="px-3 pt-3 pb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Direct messages</div>
              {users.map((u) => (
                <button key={u.id} onClick={() => openConv({ type: "dm", u })} data-testid={`chat-open-dm-${u.id}`} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent text-left transition-colors">
                  <Avatar photo={u.photo_path} name={u.name} size={40} />
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{u.name}</p><p className="text-xs text-muted-foreground capitalize truncate">{u.role}</p></div>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-muted/40" data-testid="chat-widget-messages">
                {messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No messages yet. Say hello 👋</p>}
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                      {!mine && <Avatar photo={photoMap[m.sender_id]} name={m.sender_name} size={28} />}
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"}`}>
                        {!mine && active.type === "team" && <p className="text-[11px] font-semibold mb-0.5 text-primary">{m.sender_name}</p>}
                        {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                        {isAudio(m.file) && <audio controls src={fileUrl(m.file.path)} className="mt-1 h-9 max-w-full" data-testid="chat-audio" />}
                        {m.file && !isAudio(m.file) && <a href={fileUrl(m.file.path)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 mt-1 text-xs underline"><FileText size={13} /> {m.file.name}</a>}
                        <div className="flex items-center gap-1 justify-end mt-1">
                          <span className="text-[10px] opacity-60">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                          {mine && (readByOthers(m)
                            ? <CheckCheck size={14} className="text-sky-300" data-testid="tick-read" title="Read" />
                            : <Check size={14} className="opacity-70" data-testid="tick-sent" title="Sent" />)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-2 border-t border-border flex items-center gap-1.5 bg-card">
                <input type="file" ref={fileRef} onChange={onFile} className="hidden" data-testid="chat-widget-file" />
                <Button variant="ghost" size="icon" className="rounded-full shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading || recording} data-testid="chat-widget-attach"><Paperclip size={18} /></Button>
                {recording ? (
                  <div className="flex-1 flex items-center gap-2 px-3 text-sm text-primary"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Recording voice note…</div>
                ) : (
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={uploading ? "Uploading…" : "Message…"} className="rounded-full h-9" data-testid="chat-widget-input" />
                )}
                {recording ? (
                  <Button size="icon" className="rounded-full shrink-0 bg-red-600 hover:bg-red-700" onClick={stopRec} data-testid="chat-stop-rec"><Square size={16} /></Button>
                ) : text.trim() ? (
                  <Button size="icon" className="rounded-full shrink-0" onClick={() => send()} data-testid="chat-widget-send"><Send size={16} /></Button>
                ) : (
                  <Button variant="ghost" size="icon" className="rounded-full shrink-0" onClick={startRec} disabled={uploading} data-testid="chat-rec"><Mic size={18} /></Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="chat-widget-toggle"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>
    </>
  );
}
